import { SyftFormData } from "../blocks/forms/types";
import { getCurrentPath, isFormInputElement } from "../common/utils";
import { getFormFields } from "./formUtils";

// Map of document to interval ID for the form binder
const formBinderIntervals = new Map<Document, number>();

// Set of forms that have already been bound
const boundForms = new Set<HTMLFormElement>();

// Set of inputs and buttons that have already been bound
const boundElements = new Set<
  HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement | HTMLButtonElement
>();

/**
 * Returns true when the form is attached to the page and rendered. Users can't
 * submit a form they can't see, so a hidden form being submitted is a script
 * transport (e.g. the Meta pixel POSTs its beacons through a display:none form
 * pointed at facebook.com/tr), not a user action.
 */
export function isFormRendered(form: HTMLFormElement): boolean {
  if (!form.isConnected) return false;
  const view = form.ownerDocument.defaultView;
  if (view == null) return false;
  for (let el: HTMLElement | null = form; el != null; el = el.parentElement) {
    if (el.hidden || view.getComputedStyle(el).display === "none") {
      return false;
    }
  }
  return true;
}

/**
 * Reports a form submission once. Skips hidden forms and repeats within 100ms,
 * since one user submit can reach us via the button click, the submit event,
 * and a programmatic form.submit().
 */
export function reportFormSubmit(
  form: HTMLFormElement,
  callback: (path: string, data: SyftFormData, destination?: URL) => void
): void {
  if (form.dataset.syftSubmitted === "true") return;
  if (!isFormRendered(form)) return;

  const currentPath = getCurrentPath(true);
  const destination = form.action
    ? new URL(form.action, window.location.href)
    : undefined;

  callback(
    currentPath,
    {
      attributes: { ...form.dataset },
      fields: getFormFields(form),
    },
    destination
  );

  // Mark as submitted to prevent duplicate events
  form.dataset.syftSubmitted = "true";

  // Reset the flag after a short delay
  setTimeout(() => {
    delete form.dataset.syftSubmitted;
  }, 100);
}

/**
 * Binds form submission events to a form
 */
export function bindFormSubmit(
  form: HTMLFormElement,
  callback: (path: string, data: SyftFormData, destination?: URL) => void
): void {
  if (boundForms.has(form)) return;

  form.addEventListener("submit", () => reportFormSubmit(form, callback), {
    capture: true,
  });
  boundForms.add(form);

  // Also bind submit button click events
  const submitButtons = form.querySelectorAll(
    'button[type="submit"], input[type="submit"]'
  );
  submitButtons.forEach((button) => {
    if (button instanceof HTMLElement) {
      button.addEventListener("click", () => reportFormSubmit(form, callback));
    }
  });
}

/**
 * Binds input change events to a form input
 */
export function bindFormInput(
  input: HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement,
  callback: (path: string, data: SyftFormData) => void
): void {
  if (boundElements.has(input)) return;
  const handleInputChange = (): void => {
    const currentPath = getCurrentPath(true);
    const form = input.form;

    if (form) {
      callback(currentPath, {
        attributes: { ...form.dataset },
        fields: getFormFields(form),
      });
    } else {
      // Handle standalone inputs not part of a form
      callback(currentPath, {
        attributes: { ...input.dataset },
        fields: [
          {
            id: input.id,
            name: input.name,
            type: input.type,
            tagName: input.tagName,
            value: input.value,
          },
        ],
      });
    }
  };

  input.addEventListener("input", handleInputChange);
  input.addEventListener("change", handleInputChange);
  boundElements.add(input);
}

/**
 * Scans a document for forms and inputs and binds them
 */
export function scanAndBindForms(
  doc: Document,
  formSubmitCallback?: (
    path: string,
    data: SyftFormData,
    destination?: URL
  ) => void,
  formInputCallback?: (path: string, data: SyftFormData) => void
): void {
  if (formSubmitCallback) {
    const forms = doc.querySelectorAll("form");
    forms.forEach((form) => {
      bindFormSubmit(form as HTMLFormElement, formSubmitCallback);
    });
  }

  if (formInputCallback) {
    // Bind form inputs
    const inputs = doc.querySelectorAll("input, select, textarea");
    inputs.forEach((input) => {
      if (isFormInputElement(input)) {
        bindFormInput(input, formInputCallback);
      }
    });
  }
}

/**
 * Starts the background form binder
 */
export function startFormBinder(
  doc: Document = document,
  formSubmitCallback?: (
    path: string,
    data: SyftFormData,
    destination?: URL
  ) => void,
  formInputCallback?: (path: string, data: SyftFormData) => void
): () => void {
  // Clear any existing interval for this document
  if (formBinderIntervals.has(doc)) {
    clearInterval(formBinderIntervals.get(doc));
    formBinderIntervals.delete(doc);
  }

  // Initial scan
  scanAndBindForms(doc, formSubmitCallback, formInputCallback);
  // Set up interval for continuous monitoring
  const intervalId = window.setInterval(() => {
    scanAndBindForms(doc, formSubmitCallback, formInputCallback);
  }, 5000);

  formBinderIntervals.set(doc, intervalId);

  // Return cleanup function
  return () => {
    if (formBinderIntervals.has(doc)) {
      clearInterval(formBinderIntervals.get(doc));
      formBinderIntervals.delete(doc);
    }
  };
}
